import { ChangeEvent, useEffect, useState } from 'react';
import { Link, useRefresh } from 'react-admin';
import { Row, Col } from 'antd';

import { Credit } from '@types';
import { creditHttp } from '@network';
import { showAxiosError } from '@utils';
import { GuarantorCard } from './guarantor-card';
import { Checkbox } from '@mui/material';
import cls from './credit-guarantors.module.css';
import { useRbacEdit } from '@hooks';


interface Props {
  credit: Credit;
  editable?: boolean;
}

export const CreditGuarantors = ({ credit, editable }: Props) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isGuarantorsRequired, setIsGuarantorsRequired] = useState<boolean>(false);
  const refresh = useRefresh();
  const rbacEdit = useRbacEdit();
  const guarantors = credit.guarantors || [];

  const onDelete = async (guarantorId: number) => {
    try {
      await creditHttp.deleteGuarantor(credit.id, guarantorId);
      refresh();
    } catch (err: any) {
      showAxiosError(err);
    }
  };

  const setGuarantorRequireHandler = async (e: ChangeEvent<HTMLInputElement>) => {
    const isRequired = !e.target.checked;

    try {
      setIsLoading(true);
      const updatedCredit = await creditHttp.setGuarantorRequired(
        { creditId: credit.id, isRequired },
      );
      setIsGuarantorsRequired(updatedCredit.isGuarantorsRequired || false);
      refresh();
    } catch (err: any) {
      showAxiosError(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (credit.isGuarantorsRequired !== undefined) {
      setIsGuarantorsRequired(credit.isGuarantorsRequired);
    }
  }, [credit]);

  const buyerTier = credit.user?.mainBuyer?.tier ?? 'C';
  const buyerIsOk = buyerTier === 'A' || buyerTier === 'B';

  return (
    <div className={cls.box}>
      <div className={cls.head}>
        <div className={cls.title}>Guarantors</div>
        <div className={cls.tick}>
          <Checkbox
            style={{ width: 40 }}
            size="medium"
            onChange={setGuarantorRequireHandler}
            disabled={!editable || isLoading || !rbacEdit}
            checked={!isGuarantorsRequired}
          /> This credit does not require guarantors
        </div>
        <div className={cls.tick}>
          {buyerIsOk ? <OkIcon /> : <DeniedIcon />}
          <span className={cls.tickBuyer}>Buyer Tier: {buyerTier}</span>
        </div>
      </div>

      <Row gutter={[20, 20]} className={cls.guarantors}>
        {guarantors.map((guarantor, idx) => (
          <Col xs={24} sm={12} md={6} key={guarantor.id}>
            <GuarantorCard
              guarantor={guarantor}
              onDelete={onDelete}
              editable={rbacEdit}
              linkTo={`/credit/${credit.id}?guarantor=${guarantor.id}`}
              isDisabled={!credit.isGuarantorsRequired}
            />
          </Col>
        ))}
        {credit.isGuarantorsRequired && rbacEdit ? (
          <Col xs={24} sm={12} md={6}>
            <Link to={`/credit/${credit.id}?guarantor-create`} className={cls.addLink}>
              <div className={cls.iconPlus}>+</div>
            </Link>
          </Col>
        ) : null}
      </Row>
    </div>
  );
};

const OkIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="20" height="20" rx="10" fill="#275D3C" />
    <path d="M9.28577 11.9639L13.7501 7.49952"
      stroke="white"
      strokeWidth="2"
      strokeLinecap="round" />
    <path d="M9.28577 11.9639L7.50003 10.1781"
      stroke="white"
      strokeWidth="2"
      strokeLinecap="round" />
  </svg>
);

const DeniedIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M10 0C4.47801 0 0 4.47801 0 10C0 15.522 4.47801 20 10 20C15.522 20 20 15.522 20 10C19.9911 4.47801 15.5175 0.00444247 10 0Z"
      fill="#FF4548" />
    <path d="M11.7132 9.80362L13.3947 11.5778C13.8684 12.0753 13.8684 12.8795 13.3947 13.3769C12.9211 13.8744 12.1553 13.8744 11.6816 13.3769L10 11.6027L8.31053 13.3686C7.84474 13.8661 7.07895 13.8661 6.60526 13.3686C6.13158 12.8712 6.13158 12.067 6.60526 11.5695L8.28684 9.80362L6.60526 8.02941C6.13158 7.54026 6.13158 6.73606 6.60526 6.23862C6.84211 5.9899 7.15 5.86554 7.45789 5.86554C7.76579 5.86554 8.07368 5.9899 8.31053 6.23862L10 8.00454L11.6895 6.23862C11.9263 5.9899 12.2342 5.86554 12.5421 5.86554C12.85 5.86554 13.1579 5.9899 13.3947 6.23862C13.8684 6.73606 13.8684 7.54026 13.3947 8.0377L11.7132 9.80362Z"
      fill="white" />
  </svg>
);
